import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/lib/auth";
import { MENU, canSee, APP_NAME, APP_TAGLINE_SHORT, ROLE_LABEL, APP_FOOTER, GPDP_LOGO, GPDP_LOGO_ALT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { EnvBanner } from "./EnvBanner";
import { NotificationBell } from "./NotificationBell";
import { useTheme, THEMES } from "@/lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
}

function NavList({ role, onNavigate }) {
  const location = useLocation();
  return (
    <nav className="flex flex-col gap-6 py-4 px-3">
      {MENU.map((section) => {
        const items = section.items.filter((i) => canSee(i, role));
        if (items.length === 0) return null;
        return (
          <div key={section.section}>
            <div className="text-[0.65rem] font-semibold tracking-[0.18em] uppercase text-muted-foreground px-3 mb-2">
              {section.section}
            </div>
            <ul className="space-y-0.5">
              {items.map((item) => {
                const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
                return (
                  <li key={item.key}>
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      data-testid={`nav-${item.key}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors relative ${
                        active
                          ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                          : "text-foreground/75 hover:bg-primary/5 hover:text-primary"
                      }`}
                    >
                      {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-accent" />}
                      <Icon name={item.icon} className={`h-4 w-4 shrink-0 ${active ? "text-accent" : ""}`} />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function BrandHeader() {
  return (
    <div className="px-5 py-5 border-b border-border bg-gradient-to-b from-white to-muted/30">
      <div className="flex items-center gap-3">
        <div className="relative h-12 w-12 rounded-xl bg-white ring-2 ring-primary/10 shadow-sm flex items-center justify-center overflow-hidden">
          <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-11 w-11 object-contain" data-testid="brand-sidebar-logo" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-[0.9rem] tracking-tight text-primary leading-tight">
            SIMPELKAMI
          </div>
          <div className="font-heading font-bold text-[0.75rem] tracking-tight text-primary leading-tight">
            GPDP
          </div>
          <div className="text-[0.55rem] text-muted-foreground tracking-[0.15em] uppercase mt-1 font-semibold">
            Klasis Mimika
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-1.5">
        <div className="h-1 w-6 rounded-full bg-accent" />
        <div className="text-[0.55rem] text-muted-foreground tracking-[0.14em] uppercase font-bold">Periode 2026–2031</div>
      </div>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const initials = useMemo(() => (user?.name || "?").split(" ").slice(0,2).map(s=>s[0]).join(""), [user]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <EnvBanner />
      <div className="flex flex-1 min-h-0">
        {/* Sidebar - desktop */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 xl:w-72 border-r border-border bg-white shrink-0">
          <BrandHeader />
          <div className="flex-1 overflow-y-auto">
            <NavList role={user?.role} />
          </div>
          <div className="p-3 text-[0.65rem] text-muted-foreground border-t border-border bg-muted/30">
            {APP_FOOTER}
          </div>
        </aside>

        {/* Sidebar - mobile drawer */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="left" className="p-0 w-72 bg-muted/50">
            <BrandHeader />
            <div className="overflow-y-auto h-[calc(100vh-88px)]">
              <NavList role={user?.role} onNavigate={() => setOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-md">
            <div className="flex items-center gap-3 px-4 lg:px-8 h-14">
              <Button
                variant="ghost" size="icon"
                className="lg:hidden"
                onClick={() => setOpen(true)}
                data-testid="btn-menu-mobile"
              >
                <Icons.Menu className="h-5 w-5" />
              </Button>

              <div className="flex items-center gap-2 lg:hidden">
                <div className="h-7 w-7 rounded bg-primary text-primary-foreground flex items-center justify-center font-heading text-xs font-bold">S</div>
                <div className="font-heading font-bold text-sm text-primary">SIMPEL</div>
              </div>

              <div className="hidden md:flex items-center gap-2 ml-2">
                <div className="h-1 w-8 bg-primary rounded-full" />
                <div className="h-1 w-3 bg-accent rounded-full" />
              </div>

              <div className="ml-auto flex items-center gap-2">
                <Link to="/public" target="_blank" className="hidden md:inline-flex text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="link-public-portal">
                  Portal Publik ↗
                </Link>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2 rounded-full h-9 pl-1 pr-3" data-testid="btn-user-menu">
                      <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                        {initials}
                      </span>
                      <span className="hidden sm:block text-left">
                        <span className="block text-xs font-medium leading-none">{user?.name}</span>
                        <span className="block text-[0.65rem] text-muted-foreground leading-tight mt-0.5">{ROLE_LABEL[user?.role] || user?.role}</span>
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel>
                      <div className="text-xs font-semibold">{user?.name}</div>
                      <div className="text-[0.65rem] text-muted-foreground">{user?.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                      <Icons.LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Motif Papua</DropdownMenuLabel>
                    <div className="grid grid-cols-2 gap-1 p-1" data-testid="theme-picker-grid">
                      {THEMES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setTheme(t.key)}
                          className={`text-left rounded-md p-2 text-xs transition-colors ${theme === t.key ? "bg-primary/10 border border-primary/40" : "border border-transparent hover:bg-muted/60"}`}
                          data-testid={`btn-theme-${t.key}`}
                        >
                          <div className="flex items-center gap-1 mb-1">
                            <div className="h-2 w-2 rounded-full" style={{ background: t.swatch[0] }} />
                            <div className="h-2 w-2 rounded-full" style={{ background: t.swatch[1] }} />
                          </div>
                          <div className={`font-semibold ${theme === t.key ? "text-primary" : ""}`}>{t.label}</div>
                          <div className="text-[0.6rem] text-muted-foreground truncate">{t.desc}</div>
                        </button>
                      ))}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                      <Icons.LogOut className="h-4 w-4 mr-2" /> Keluar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto">
            <div className="max-w-[1400px] mx-auto p-4 lg:p-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
